<script setup>
import HospitalList from "@/components/hpo/dashboard/hospital/HospitalList";
import {useQuery, useMutation} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {IzsPatientListQuery} from "@/graphql/queries/hpo/patients.graphql"
import {HpoFillDummyPatientsMutation} from "@/graphql/mutations/hpo/patient/fillDummyPatients.graphql"

import {HpoHospitalList} from "@/graphql/queries/hpo/hpoHospitalList.graphql"
import {HpoBodyStatus, HpoRole, HpoTransportStatus, PatientModalTabs} from "../choices";
import {computed, onMounted, ref, useTemplateRef, watch} from "vue";
import CountsBlock from "@/components/hpo/dashboard/CountsBlock";

import DestinationHospitalModal from "./DestinationHospitalModal.vue";
import TransportReplyModal from "./patientListItems/TransportReplyModal.vue";
import TicPatientList from "./patientListItems/TicPatientList.vue";
import AllPersonsList from "./patientListItems/AllPersonsList.vue";
import AddInjuredAndUninjuredButtons from "./AddInjuredAndUninjuredButtons.vue";
import dayjs from "dayjs";
import PatientListTab from "./patientListTabs/PatientListTab.vue";
import {mdiChevronDoubleDown, mdiDownload, mdiRun} from "@mdi/js";
import {useDisplay} from 'vuetify'
import {useSearch} from "@/search";
import EstimatedOnSceneModal from "./EstimatedOnSceneModal.vue";
import DashboardModalsContainer from "@/components/hpo/dashboard/DashboardModalsContainer.vue";
import EvacManagerPatientList from "@/components/hpo/dashboard/patientListItems/EvacManagerPatientList.vue";
import Modal from "@/components/modal/Modal.vue";
import QrScanButton from "@/components/hpo/QrScanButton.vue";

const PRIORITY_ORDER = ["IIA", "I_IIA", "I_IIB", "IIB", "III", "IV"];

const props = defineProps({
  hpo: {
    type: Object,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  patientCounts: {
    type: Object,
    required: true
  },
  patientCountsLoading: {
    type: Boolean,
    required: true
  },
  hpoPassword: {
    type: String,
    required: true
  },
  countries: {
    type: Array,
    required: true
  },
  hpoPin: {
    type: Number,
    required: true
  },
  mobileView: {
    type: Boolean,
    required: true,
  },
  chipProperties: {
    type: Object,
    required: true
  },
  personsTrackerTimestamp: {
    type: Object,
    required: true
  },
  hospitalsTrackerTimestamp: {
    type: Object,
    required: false
  }
})

const modals = useTemplateRef('modals');

const editedTransportData = ref(null)
const transportRequestReplyData = ref(null)

const now = ref(new dayjs())
const {smAndUp} = useDisplay()

const lastClickedTab = ref(HpoBodyStatus.AT_SCENE)
const activeTab = ref(lastClickedTab.value)
const tab = computed({
  get() {
    return activeTab.value
  },
  set(newValue) {
    lastClickedTab.value = newValue
    activeTab.value = newValue
  }
})

const {mutate, loading: mutationLoading} = useMutation(HpoFillDummyPatientsMutation)

// Persons Query
const personsQueryVariables = computed(() => ({
  updateTimestamp: props.personsTrackerTimestamp,
  hpoId: props.hpo.id
}));
const {result: patientListResult, loading: patientListLoading} = useQuery(IzsPatientListQuery, personsQueryVariables)
const patientList = useLastResult(patientListResult, [], data => data.izsPatientList)
const alertList = useLastResult(patientListResult, [], data => data.alertList.alerts)

// Hospitals Query
const hospitalsQueryVariables = computed(() => ({
  updateTimestamp: props.hospitalsTrackerTimestamp,
  hpoId: props.hpo.id
}));
const {result: hospitalsResult, loading: hospitalListLoading} = useQuery(HpoHospitalList, hospitalsQueryVariables)
const hospitalList = useLastResult(hospitalsResult, [], data => data.hpoHospitalList)


const anyLoading = computed(() => {
  return hospitalListLoading.value || props.patientCountsLoading || patientListLoading.value
})

const sortedTicPatientList = computed(() => {
  // Sorts patient list first by priority, then by TIC
  if (patientList.value.length > 0) {
    // Only patients with TIC number
    const ticPatients = patientList.value.filter((patient) => patient.tic !== null)
    return ticPatients.slice().sort((a, b) => {
      // First, sort by priority based on the custom order
      const priorityComparison = PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority);
      // If priorities are the same, sort by tic as a secondary criterion
      if (priorityComparison === 0) {
        return a.tic.localeCompare(b.tic);
      }
      return priorityComparison;
    });
  }
  return []
})


const patientsAtScene = computed(() => {
  return sortedTicPatientList.value.filter((patient) => {
    return patient.status === HpoBodyStatus.AT_SCENE
      || patient.status === HpoBodyStatus.TRANSPORT_PENDING
      || patient.status === HpoBodyStatus.TRANSPORT_READY
  })
})

const patientsInTransport = computed(() => {
  return sortedTicPatientList.value.filter((patient) => patient.status === HpoBodyStatus.IN_TRANSPORT)
})

const patientsInHospital = computed(() => {
  return sortedTicPatientList.value.filter((patient) => patient.status === HpoBodyStatus.IN_HOSPITAL)
})

const patientsReleased = computed(() => {
  return sortedTicPatientList.value.filter((patient) => patient.status === HpoBodyStatus.RELEASED)
})

const patientsWithAlarm = computed(() => {
  return sortedTicPatientList.value.filter((patient) => alertList.value.includes(parseInt(patient.id)))
})

const showAllPatientsModal = ref(false);

const patientsForEvacManager = computed(() => {
  return sortedTicPatientList.value.filter((patient) => {
    return patient.status === HpoBodyStatus.TRANSPORT_PENDING
      || patient.status === HpoBodyStatus.TRANSPORT_READY
      || alertList.value.includes(parseInt(patient.id))
  });
});

const transportDrafts = computed(() => {
  return sortedTicPatientList.value.filter((patient) => {
    return patient.transportStatus === HpoTransportStatus.DRAFT;
  });
});

const {query, searchResultsOrEmpty: patientsSearchResult, isSearching} = useSearch(
  patientList, ['firstName', 'lastName', 'birthNumber', 'birthYear', 'note'], ['tic']
);

watch(query, async (newValue, previousValue) => {
  if (newValue) {
    activeTab.value = 'search'
  } else {
    activeTab.value = lastClickedTab.value
  }
});

const tabs = computed(() => {
  return [
    {
      label: pgettext('hpo_body_status', 'at scene'),
      value: HpoBodyStatus.AT_SCENE,
      badgeContent: patientsAtScene.value.length,
    },
    {
      label: pgettext('hpo_body_status', 'in transport'),
      value: HpoBodyStatus.IN_TRANSPORT,
      badgeContent: patientsInTransport.value.length,
    },
    {
      label: pgettext('hpo_body_status', 'in hospital'),
      value: HpoBodyStatus.IN_HOSPITAL,
      badgeContent: patientsInHospital.value.length,
    },
    {
      label: pgettext('hpo_body_status', 'released'),
      value: HpoBodyStatus.RELEASED,
      badgeContent: patientsReleased.value.length,
    },
    {
      label: pgettext('label', 'all effected'),
      value: 'all',
      badgeContent: patientList.value.length,
    },
    {
      label: pgettext('noun', 'search'),
      value: "search",
      badgeContent: patientsSearchResult.value.length,
    }]
})

const dropdownActiveTab = computed(() => {
  if (tab.value === 'alarm') {
    return {
      label: pgettext('label', 'Patients lists'),
      badgeContent: null
    }
  }
  return tabs.value.find((item) => item.value === tab.value)
})

function displayTriageWizard(patient) {
  modals.value.showTriageWizard(patient);
}

function displayTransportWizard(patient) {
  modals.value.showTransportWizard(patient);
}

function displayIdentificationWizard(patient) {
  modals.value.showIdentificationWizard(patient);
}

function displayPatientModal(data) {
  modals.value.showPatientModal(data);
}

function displayActionsModal(patient) {
  modals.value.showActionsModal(patient);
}

function handleOpenReplyModal(reply, patient) {
  transportRequestReplyData.value = {
    reply: reply,
    patient: patient
  }
}

function updateNow() {
  setInterval(() => {
    now.value = new dayjs()
    if (props.hpo.test === true) {
      autoUpdateTransports(now.value)
    }
  }, 5000)
}

function fillDummyPatients() {
  mutate({hpoId: props.hpo.id})
}

function autoUpdateTransports(now) {
  for (const patient of patientList.value) {
    if (patient.transportStatus === HpoTransportStatus.DISP_PENDING) {
      const timestamp = new dayjs(patient.timestampLastUpdate)
      if (now.diff(timestamp, 'second') > 10) {
        hpoSetDestinationHospital({
          patientId: patient.id,
          priority: patient.priority,
          hospital: props.hospitalList[0].id,
          transportMinutes: 5
        })
      }
    } else if (patient.transportStatus === HpoTransportStatus.HOSPITAL_PENDING) {
      const timestamp = new dayjs(patient.timestampLastUpdate)
      if (now.diff(timestamp, 'second') > 10) {
        mutate({
          patientId: patient.id,
          hospitalId: patient.hospital.id,
          testContactPointId: props.hpo.testContactPointId,
          status: 2,
          rejectionReason: null
        })
      }
    } else if (patient.eta) {
      const etaMinutes = patient.eta.diff(now, 'minute')
      if (etaMinutes < 1) {
        setPatientInHospitalMutation({
          patientId: patient.id,
          hospitalId: patient.hospital.id
        })
      }
    }
  }
}


onMounted(() => {
  updateNow()
})

function clearSearch() {
  query.value = '';
}

function handleSubmitTransport() {
  if (props.member.role === HpoRole.EVAC_MANAGER) {
    clearSearch();
  }
}

defineExpose({clearSearch});

</script>

<template>
  <v-container v-if="member.role !== HpoRole.EVAC_MANAGER" fluid class="pa-0">
    <v-row dense>
      <v-col cols="12" class="d-flex ga-3 flex-wrap">
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Estimated on scene')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.onScene"
                     :modalComponent="EstimatedOnSceneModal"
                     :legend="pgettext('label', 'Estimated number of persons on scene')"/>

        <HospitalList v-if="[HpoRole.ZOS, HpoRole.CONTACT_POINT].includes(member.role)"
                      :hpoId="hpo.id"
                      :hospitalList="hospitalList"
                      :member="member"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col>
        <v-row dense>
          <v-col cols="12" lg="2" xxl="1" class="d-flex flex-column ga-2">
            <template v-if="member.role === HpoRole.ZOS">
              <v-btn :href="reverseUrl('web:hpo:download_patients', hpo.id)"
                     :prependIcon="mdiDownload">
                {{ pgettext('action', 'Export patients') }}
              </v-btn>
              <v-btn @click="fillDummyPatients" variant="outlined"
                     :loading="mutationLoading"
                     text="FILL"></v-btn>

              <v-btn :min-height="48"
                     class="hpo-dashboard-evac-manager__action-button"
                     size="large"
                     @click="displayTransportWizard()">
                <strong>{{ pgettext('action', 'Transport Patient') }}</strong>
              </v-btn>
              <v-btn :min-height="48"
                     size="large"
                     class="hpo-dashboard-evac-manager__action-button"
                     @click="displayTriageWizard()">
                <strong>{{ pgettext('action', 'Add Patient') }}</strong>
              </v-btn>

              <v-btn :text="pgettext('label', 'Add Uninjured')"
                     :min-height="48"
                     class="font-weight-bold"
                     :prepend-icon="mdiRun"
                     color="success"
                     @click="displayIdentificationWizard({skipTic: true})"></v-btn>
            </template>
          </v-col>
          <v-col cols="12" lg="10" xxl="11">
            <v-card>
              <v-card-text class="pa-1">
                <v-row dense>
                  <v-col cols="auto">
                    <QrScanButton @scan="query = $event.tic"/>
                  </v-col>
                  <v-col>
                    <!-- SEARCH BAR -->
                    <v-text-field clearable
                                  variant="outlined"
                                  :hide-spin-buttons="true"
                                  hide-details
                                  :loading="patientListLoading"
                                  :label="pgettext('title', 'Patient search')"
                                  :placeholder="pgettext('label', 'Put in TIC Card number / name / birth year / birth number / note')"
                                  v-model="query"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <v-card>
                      <div class="d-flex">
                        <PatientListTab label="ALARM" value="alarm"
                                        variant="outlined"
                                        :badgeContent="patientsWithAlarm.length"
                                        :doAlert="patientsWithAlarm.length > 0"
                                        :class="{'v-tab--selected': tab==='alarm'}"
                                        @click="tab='alarm'"/>
                        <v-tabs v-model="tab" center-active show-arrows grow>

                          <template v-for="item in tabs">
                            <PatientListTab v-show="smAndUp"
                                            :label="item.label"
                                            :value="item.value"
                                            :badgeContent="item.badgeContent"/>
                          </template>


                          <v-menu v-if="!smAndUp">
                            <template v-slot:activator="{ props }">
                              <v-btn
                                class="align-self-center me-4"
                                height="100%"
                                rounded="0"
                                variant="outlined"
                                v-bind="props"
                              >
                                <template v-slot:append
                                          v-if="dropdownActiveTab.badgeContent !== null">
                                  <v-badge
                                    color="info"
                                    :content="dropdownActiveTab.badgeContent"
                                    inline
                                  ></v-badge>
                                </template>
                                {{ dropdownActiveTab.label }}

                                <template v-slot:prepend>
                                  <v-icon :icon="mdiChevronDoubleDown" end></v-icon>
                                </template>
                              </v-btn>
                            </template>

                            <v-list class="bg-grey-lighten-3">
                              <v-list-item
                                v-for="item in tabs"
                                :key="item.value"
                                :title="item.label"
                                @click="tab = item.value">

                                <template v-slot:append>
                                  <v-badge
                                    color="info"
                                    :content="item.badgeContent"
                                    inline
                                  ></v-badge>
                                </template>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-tabs>

                      </div>
                      <v-card-text>
                        <v-tabs-window v-model="tab">
                          <v-tabs-window-item value="alarm" :transition="false">
                            <TicPatientList :patientList="patientsWithAlarm"
                                            :hpo="hpo"
                                            :member="member"
                                            :anyLoading="anyLoading"
                                            :hospitalList="hospitalList"
                                            :alertList="alertList"
                                            :now="now"
                                            @displayActionsModal="displayActionsModal"
                                            @openReplyModal="handleOpenReplyModal"/>
                          </v-tabs-window-item>

                          <v-tabs-window-item :value="HpoBodyStatus.AT_SCENE"
                                              :transition="false">
                            <TicPatientList :patientList="patientsAtScene"
                                            :hpo="hpo"
                                            :member="member"
                                            :anyLoading="anyLoading"
                                            :hospitalList="hospitalList"
                                            :alertList="alertList"
                                            :now="now"
                                            @displayActionsModal="displayActionsModal"
                                            @openReplyModal="handleOpenReplyModal"/>
                          </v-tabs-window-item>

                          <v-tabs-window-item :value="HpoBodyStatus.IN_TRANSPORT"
                                              :transition="false">
                            <TicPatientList :patientList="patientsInTransport"
                                            :hpo="hpo"
                                            :member="member"
                                            :anyLoading="anyLoading"
                                            :hospitalList="hospitalList"
                                            :alertList="alertList"
                                            :now="now"
                                            @displayActionsModal="displayActionsModal"
                                            @openReplyModal="handleOpenReplyModal"/>
                          </v-tabs-window-item>

                          <v-tabs-window-item :value="HpoBodyStatus.IN_HOSPITAL"
                                              :transition="false">
                            <TicPatientList :patientList="patientsInHospital"
                                            :hpo="hpo"
                                            :member="member"
                                            :anyLoading="anyLoading"
                                            :hospitalList="hospitalList"
                                            :alertList="alertList"
                                            :now="now"
                                            @displayActionsModal="displayActionsModal"
                                            @openReplyModal="handleOpenReplyModal"/>
                          </v-tabs-window-item>

                          <v-tabs-window-item value="search" :transition="false">
                            <AllPersonsList :patientList="patientsSearchResult"
                                            :hpo="hpo"
                                            :member="member"
                                            :anyLoading="anyLoading"
                                            :hospitalList="hospitalList"
                                            :alertList="alertList"
                                            :now="now"
                                            :countries="countries"
                                            @displayActionsModal="displayActionsModal"/>
                          </v-tabs-window-item>

                          <v-tabs-window-item value="all" :transition="false">
                            <AllPersonsList :patientList="patientList"
                                            :hpo="hpo"
                                            :member="member"
                                            :anyLoading="anyLoading"
                                            :hospitalList="hospitalList"
                                            :alertList="alertList"
                                            :now="now"
                                            :countries="countries"
                                            @displayActionsModal="displayActionsModal"/>
                          </v-tabs-window-item>
                        </v-tabs-window>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>

  <!-- View for evac. manager -->
  <section v-else class="hpo-dashboard-evac-manager">
    <aside class="hpo-dashboard-evac-manager__sidebar">
      <CountsBlock :hpoId="hpo.id"
                   :title="pgettext('label', 'Estimated on scene')"
                   :member="member"
                   :loading="patientCountsLoading"
                   :patientCounts="patientCounts.onScene"
                   :modalComponent="EstimatedOnSceneModal"/>

      <nav class="hpo-dashboard-evac-manager__action-button-grid">
        <v-btn :min-height="48"
               class="hpo-dashboard-evac-manager__action-button"
               @click="displayTransportWizard()">
          <strong>{{ pgettext('action', 'Transport Patient') }}</strong>
        </v-btn>
        <v-btn :min-height="48"
               class="hpo-dashboard-evac-manager__action-button"
               @click="displayTriageWizard()">
          <strong>{{ pgettext('action', 'Add Patient') }}</strong>
        </v-btn>
        <v-btn :min-height="48"
               class="hpo-dashboard-evac-manager__action-button"
               @click="displayIdentificationWizard()">
          <strong>{{ pgettext('action', 'Identify Patient') }}</strong>
        </v-btn>
        <v-btn :min-height="48"
               class="hpo-dashboard-evac-manager__action-button"
               @click="showAllPatientsModal = true">
          <strong>{{ pgettext('action', 'All Patients List') }}</strong>
        </v-btn>
      </nav>
    </aside>
    <main class="hpo-dashboard-evac-manager__main">
      <section>
        <h2 class="mb-2">{{ pgettext('title', 'Patients for Transport') }}</h2>

        <EvacManagerPatientList v-if="patientsForEvacManager.length > 0"
                                :now="now"
                                :alertList="alertList"
                                :patientList="patientsForEvacManager"
                                :hospitalList="hospitalList"
                                :member="member"
                                :hpo="hpo"
                                @displayActionsModal="displayActionsModal"
                                @openReplyModal="handleOpenReplyModal"
                                @displayTransportWizard="displayTransportWizard"/>
        <v-empty-state v-else
                       :title="pgettext('message', 'When a patient is ready to be transported, request a transport by clicking Transport patient.')"
                       color="primary">
          <template #actions>
            <v-btn :min-height="64" class="px-8"
                   @click="displayTransportWizard()">
              <strong>{{ pgettext('action', 'Transport Patient') }}</strong>
            </v-btn>
          </template>
        </v-empty-state>
      </section>

      <section v-if="transportDrafts.length > 0" class="mt-6">
        <h2 class="mb-2">{{ pgettext('title', 'Transport Request Drafts') }}</h2>

        <EvacManagerPatientList :now="now"
                                :alertList="alertList"
                                :patientList="transportDrafts"
                                :hospitalList="hospitalList"
                                :member="member"
                                :hpo="hpo"
                                @displayActionsModal="displayActionsModal"
                                @openReplyModal="handleOpenReplyModal"
                                @displayTransportWizard="displayTransportWizard"/>
      </section>
    </main>

    <Modal v-if="showAllPatientsModal" :title="pgettext('title', 'All Patients List')" fullscreen
           @close="showAllPatientsModal = false">
      <v-container>
        <v-row dense>
          <v-col>
            <v-text-field clearable
                          variant="outlined"
                          :hide-spin-buttons="true"
                          :loading="patientListLoading"
                          :label="pgettext('title', 'Patient search')"
                          :placeholder="pgettext('label', 'Put in TIC Card number / name / birth year / birth number / note')"
                          v-model="query"
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <QrScanButton @scan="query = $event.tic"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <AllPersonsList :patientList="isSearching ? patientsSearchResult : patientList"
                            :hpo="hpo"
                            :member="member"
                            :anyLoading="anyLoading"
                            :hospitalList="hospitalList"
                            :alertList="alertList"
                            :now="now"
                            :countries="countries"
                            @displayActionsModal="displayActionsModal"/>
          </v-col>
        </v-row>
      </v-container>
    </Modal>
  </section>

  <DashboardModalsContainer ref="modals"
                            :countries="countries"
                            :member="member"
                            :hpo="hpo"
                            :patientList="patientList"
                            @submitTransport="handleSubmitTransport"/>

  <DestinationHospitalModal v-if="editedTransportData"
                            :patient="editedTransportData"
                            :hospitalList="hospitalList"
                            @close="editedTransportData=null"/>

  <TransportReplyModal
    v-if="transportRequestReplyData"
    :reply="transportRequestReplyData.reply"
    :patient="transportRequestReplyData.patient"
    @close="transportRequestReplyData = null"
  />
</template>
